import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import { useLang } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import DOMPurify from "dompurify";

export default function GuideDetail() {
    const { t, lang } = useLang();
    const { slug } = useParams();
    const [guide, setGuide] = useState(null);
    const [trails, setTrails] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/guide/${slug}`)
            .then(async (r) => {
                setGuide(r.data);
                const seoT = (lang === "en" && r.data.seo_title_en) ? r.data.seo_title_en : r.data.seo_title;
                const seoD = (lang === "en" && r.data.seo_description_en) ? r.data.seo_description_en : r.data.seo_description;
                if (seoT) document.title = seoT;
                if (seoD) {
                    let meta = document.querySelector('meta[name="description"]');
                    if (!meta) {
                        meta = document.createElement("meta");
                        meta.setAttribute("name", "description");
                        document.head.appendChild(meta);
                    }
                    meta.setAttribute("content", seoD);
                }
                const slugs = r.data.related_trails || [];
                if (slugs.length) {
                    const all = await api.get("/trails");
                    setTrails((all.data || []).filter((tr) => slugs.includes(tr.slug)));
                }
            })
            .catch(() => setGuide(null))
            .finally(() => setLoading(false));
    }, [slug, lang]);

    if (loading) return <div className="max-w-[900px] mx-auto px-5 py-28 text-zinc-400 uppercase tracking-wider">{t("trailDetail.loading")}</div>;
    if (!guide) return (
        <div className="max-w-[900px] mx-auto px-5 py-28">
            <p className="font-display text-5xl uppercase">{t("guide.notFound")}</p>
            <Link to="/guide" className="text-abnormal-acid uppercase tracking-wider mt-4 inline-block">← {t("guide.back")}</Link>
        </div>
    );

    const title = (lang === "en" && guide.title_en) ? guide.title_en : guide.title;
    const subtitle = (lang === "en" && guide.subtitle_en) ? guide.subtitle_en : guide.subtitle;
    const intro = (lang === "en" && guide.intro_en) ? guide.intro_en : guide.intro;
    const body = (lang === "en" && guide.body_html_en) ? guide.body_html_en : guide.body_html;
    const safeBody = { __html: DOMPurify.sanitize(body || "", { ADD_ATTR: ["target"] }) };

    return (
        <div data-testid="guide-detail-page">
            <SEO
                title={title}
                description={subtitle || intro || (lang === "en" ? "Argentario MTB Guide" : "Guida MTB Argentario")}
                image={guide.hero_image ? absolutizeUrl(guide.hero_image) : undefined}
                path={`/guide/${guide.slug}`}
                type="article"
            />
            {/* Hero */}
            <section className="relative h-[55vh] min-h-[420px] w-full overflow-hidden">
                {guide.hero_image && (
                    <img src={absolutizeUrl(guide.hero_image)} alt={title} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
                <div className="relative z-10 h-full max-w-[1100px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-14">
                    <Link to="/guide" className="text-xs uppercase tracking-[0.3em] text-zinc-300 hover:text-abnormal-acid inline-flex items-center gap-2 mb-6" data-testid="back-to-guides">
                        <ArrowLeft size={14} /> {t("guide.back")}
                    </Link>
                    <p className="text-xs tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3 inline-flex items-center gap-2">
                        <BookOpen size={14} /> {guide.season ? guide.season.toUpperCase() : t("guide.eyebrow")}
                    </p>
                    <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] rise-in">{title}</h1>
                    {subtitle && <p className="mt-3 font-display text-2xl md:text-3xl uppercase text-zinc-300">{subtitle}</p>}
                </div>
            </section>

            <section className="max-w-[820px] mx-auto px-5 md:px-10 py-14 md:py-16">
                {intro && (
                    <p data-testid="guide-intro" className="text-lg leading-relaxed text-zinc-200 mb-10 border-l-2 border-abnormal-acid pl-6">
                        {intro}
                    </p>
                )}

                <div
                    data-testid="guide-body"
                    className="prose-guide text-zinc-200 text-base md:text-lg leading-relaxed space-y-5"
                    dangerouslySetInnerHTML={safeBody}
                />

                {trails.length > 0 && (
                    <div data-testid="guide-related-trails" className="mt-14 border-t border-white/10 pt-10">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-5">{t("guide.relatedTrails")}</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {trails.map((tr) => {
                                const trName = (lang === "en" && tr.name_en) ? tr.name_en : tr.name;
                                const trSummary = (lang === "en" && tr.summary_en) ? tr.summary_en : tr.summary;
                                return (
                                <Link key={tr.slug} to={`/trail/${tr.slug}`} data-testid={`guide-trail-${tr.slug}`}
                                    className="group bg-abnormal-charcoal border border-white/10 hover:border-abnormal-acid transition-colors p-4 flex items-center gap-4">
                                    {tr.image_url && (
                                        <img src={absolutizeUrl(tr.image_url)} alt={trName} className="w-20 h-20 object-cover" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{tr.terrain} · {tr.difficulty}</p>
                                        <p className="font-display text-xl uppercase leading-tight mt-1 truncate">{trName}</p>
                                        <p className="text-xs text-zinc-400 mt-1 truncate">{trSummary}</p>
                                    </div>
                                    <ArrowRight size={16} className="text-zinc-600 group-hover:text-abnormal-acid shrink-0" />
                                </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
